/**
 * Admin-Only Feature Visibility Panel
 *
 * Compact panel rendered inside the Admin Console. Admin can toggle
 * which user-releasable analytical features are visible to normal
 * authenticated users. Backend enforces the same state — this panel
 * only reflects and mutates it.
 *
 * Guardrails (mirrored from backend service):
 *   • Only features returned by the backend registry are shown.
 *   • Failing to load => empty list; admin sees an inline error.
 *   • Admin Console + other administration functions are NEVER in
 *     the registry and can NEVER be exposed via this panel.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, ShieldCheck, Users } from 'lucide-react';
import FeatureGrantsSubPanel from '@/components/FeatureGrantsSubPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminFeatureVisibilityPanel() {
  const { token, user } = useAuth();
  const [features, setFeatures] = useState([]);   // [{key, display_name, description, visible_to_all_users, default_visible_to_users}]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState({});       // {[key]: bool}

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get(
        `${BACKEND_URL}/api/admin/feature-visibility`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFeatures(resp.data?.features || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load feature visibility');
      setFeatures([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (key, next) => {
    setSaving((s) => ({ ...s, [key]: true }));
    // Optimistic update — the checkbox flips immediately.
    setFeatures((prev) => prev.map((f) =>
      f.key === key ? { ...f, visible_to_all_users: next } : f
    ));
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/feature-visibility/${encodeURIComponent(key)}`,
        { visible_to_all_users: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${next ? 'Released to all users' : 'Restricted to admin'}`);
    } catch (e) {
      // Roll back on failure — never leave the UI ahead of the server.
      setFeatures((prev) => prev.map((f) =>
        f.key === key ? { ...f, visible_to_all_users: !next } : f
      ));
      toast.error(e?.response?.data?.detail || 'Failed to update visibility');
    } finally {
      setSaving((s) => { const c = { ...s }; delete c[key]; return c; });
    }
  };

  if (!user?.is_admin) return null;   // defence-in-depth (route already admin-gated)

  return (
    <Card className="border-slate-200" data-testid="admin-feature-visibility-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base text-[#0891b2] flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" />
          Admin-Only Feature Visibility
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Release eligible analytical features to all authenticated users. Administrative functions
          (Admin Console, user management, permissions, etc.) are never eligible and cannot appear here.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-3" data-testid="afv-loading">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
          </div>
        ) : error ? (
          <div className="text-sm text-red-600 py-3" data-testid="afv-error">{error}</div>
        ) : features.length === 0 ? (
          <div className="text-sm text-slate-500 italic py-3" data-testid="afv-empty">
            No user-releasable analytical features are registered.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="afv-table">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                  <th className="text-left py-2 px-2 font-semibold">Feature</th>
                  <th className="text-left py-2 px-2 font-semibold">Current Access</th>
                  <th className="text-right py-2 px-2 font-semibold">Visible to all users</th>
                </tr>
              </thead>
              <tbody>
                {features.map((f) => {
                  const on = f.visible_to_all_users === true;
                  const isSaving = !!saving[f.key];
                  return (
                    <tr key={f.key} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`afv-row-${f.key}`}>
                      <td className="py-2 px-2 align-top">
                        <div className="font-medium text-slate-800">{f.display_name}</div>
                        {f.description && (
                          <div className="text-xs text-slate-500 mt-0.5 max-w-md">{f.description}</div>
                        )}
                        {/* Per-user grants live inline with the feature
                            they modify — hidden once the toggle above
                            releases the feature to every authenticated
                            user (grants become moot). */}
                        {!on && (
                          <FeatureGrantsSubPanel
                            featureKey={f.key}
                            featureLabel={f.display_name}
                          />
                        )}
                      </td>
                      <td className="py-2 px-2 align-top">
                        {on ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-semibold" data-testid={`afv-label-${f.key}`}>
                            <Users className="w-3 h-3" /> ALL AUTHENTICATED USERS
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 text-[11px] font-semibold" data-testid={`afv-label-${f.key}`}>
                            <ShieldCheck className="w-3 h-3" /> ADMIN ONLY
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-2 align-top text-right">
                        <div className="inline-flex items-center gap-2">
                          {isSaving && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
                          <Checkbox
                            checked={on}
                            disabled={isSaving}
                            onCheckedChange={(v) => handleToggle(f.key, v === true)}
                            data-testid={`afv-toggle-${f.key}`}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-[10px] text-slate-400 italic mt-3">
              Admin always retains access regardless of this setting. Missing configuration fails closed.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
